import React, { type ReactNode, useMemo } from 'react';

import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import CommentIcon from '@atlaskit/icon/core/comment';
import EditIcon from '@atlaskit/icon/core/edit';
import EmailIcon from '@atlaskit/icon/core/email';
import ShareIcon from '@atlaskit/icon/core/share';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import {
	type ExternalMessageIntegration,
	type SocialSignalType,
} from '../../../../common/types/social-signals';
import { Avatar } from '../../../../common/ui/avatar';

import { ExternalMessageIntegrationButton } from './external-message-integration-button';
import { messages } from './messages';
import { timeUnitAgoMessages } from './time-unit-ago-messages';

export interface PersonCardProps {
	/**
	 * When the signal is timestamped for.
	 * If no value is given, the relative time header is not rendered.
	 **/
	signalDate?: Date;
	signalType?: SocialSignalType;
	user: {
		name: string;
		/**
		 * If nullish: the default avatar will be rendered.
		 **/
		profilePicUrl?: string | null;
		/**
		 * The user's job title within the organisation.
		 * @example "Software engineer"
		 **/
		jobTitle?: string | null;
		/**
		 * URL to go to the user's profile.
		 * If not provided, the view profile button is not rendered.
		 * NOTE: will be up to the application to supply as this URL is different
		 * 	accross products, e.g. user's profile page is different in Confluence to
		 * 	Atlassian Home.
		 **/
		profileUrl?: string | null;
	};
	externalMessageIntegration?: ExternalMessageIntegration;
	/**
	 * Called when the user clicks the email button.
	 * If not provided, the copy email button is not rendered.
	 **/
	onCopyEmail?: VoidFunction;
}

const cardXcss = xcss({
	backgroundColor: 'elevation.surface.raised',
	borderRadius: token('radius.large'),
	padding: 'space.200',
	minWidth: '214px',
	width: 'fit-content',
});

const headerXcss = xcss({
	font: 'font.body.small',
	fontWeight: 'font.weight.semibold',
});

const userNameXcss = xcss({
	font: 'font.body.large',
	fontWeight: 'font.weight.semibold',
	textAlign: 'center',
	maxWidth: '214px',
	overflowWrap: 'break-word',
});

const userSubheadingXcss = xcss({
	font: 'font.body',
	textAlign: 'center',
	maxWidth: '214px',
	overflowWrap: 'break-word',
});

export const PersonCard = ({
	signalDate,
	signalType,
	user,
	onCopyEmail,
	externalMessageIntegration,
}: PersonCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex
			direction="column"
			alignItems="center"
			gap="space.200"
			xcss={cardXcss}
			testId="search-person-card"
		>
			{signalDate && signalType && (
				<Flex xcss={headerXcss} alignItems="center" gap="space.075">
					<RelativeTimeHeader signalDate={signalDate} signalType={signalType} />
				</Flex>
			)}

			<Box>
				<Avatar
					variant={
						user.profilePicUrl
							? { type: 'profile-pic', src: user.profilePicUrl, name: user.name }
							: { type: 'initials', name: user.name }
					}
					size="xlarge"
				/>
			</Box>

			<Flex direction="column" alignItems="center" gap="space.100">
				<Box xcss={userNameXcss}>{user.name}</Box>
				{user.jobTitle && <Box xcss={userSubheadingXcss}>{user.jobTitle}</Box>}
			</Flex>

			<Flex gap="space.050">
				{externalMessageIntegration && (
					<ExternalMessageIntegrationButton integrationInfo={externalMessageIntegration} />
				)}

				{onCopyEmail && (
					<IconButton
						icon={EmailIcon}
						label={formatMessage(messages.emailButtonLabel)}
						isTooltipDisabled={false}
						onClick={onCopyEmail}
					/>
				)}

				{user.profileUrl && (
					<LinkButton href={user.profileUrl} target="_blank">
						<FormattedMessage {...messages.viewProfile} />
					</LinkButton>
				)}
			</Flex>
		</Flex>
	);
};

interface RelativeTimeHeaderProps {
	signalDate: Date;
	signalType: SocialSignalType;
}

const SECONDS_IN_A_DAY = 60 * 60 * 24;
const SECONDS_IN_AN_HOUR = 60 * 60;
type TimeSinceSignal = {
	daysSinceUpdate: number;
	/** The hours that didn't fit into a whole day */
	leftoverHoursSinceUpdate: number;
	/** The seconds since the update, e.g. 86,400 seconds for a day ago. */
	minutesSinceUpdate: number;
};

export function getTimeSinceSignal(signalDate: Date): TimeSinceSignal {
	const secondsSinceUpdate = (Date.now() - signalDate.getTime()) / 1000;
	const daysSinceUpdate = Math.floor(secondsSinceUpdate / SECONDS_IN_A_DAY);

	/** Seconds that don't fit into a whole day */
	const remainingSecondsInAnHour = secondsSinceUpdate % SECONDS_IN_A_DAY;
	const leftoverHoursSinceUpdate = Math.floor(remainingSecondsInAnHour / SECONDS_IN_AN_HOUR);
	const minutesSinceUpdate = Math.floor(secondsSinceUpdate / 60);
	return { daysSinceUpdate, leftoverHoursSinceUpdate, minutesSinceUpdate };
}

const SOCIAL_SIGNAL_ICONS: Record<SocialSignalType, ReactNode> = {
	comment: <CommentIcon label="" color="currentColor" />,
	edit: <EditIcon label="" color="currentColor" />,
	share: <ShareIcon label="" color="currentColor" />,
};

/**
 * Format the time since the social signal happened.
 * @example "Shared 6 days ago by", "Edited yesterday by", "Commented 2 hours ago by", "Shared 1 minute ago by"
 **/
export const RelativeTimeHeader = ({
	signalDate,
	signalType,
}: RelativeTimeHeaderProps): React.JSX.Element => {
	const icon = SOCIAL_SIGNAL_ICONS[signalType];
	const timeUnitAgoMessage = timeUnitAgoMessages[signalType];

	const { unit, value } = useMemo(() => {
		const { daysSinceUpdate, leftoverHoursSinceUpdate, minutesSinceUpdate } =
			getTimeSinceSignal(signalDate);
		return getTimeValues(daysSinceUpdate, leftoverHoursSinceUpdate, minutesSinceUpdate);
	}, [signalDate]);

	return (
		<>
			{icon}
			<FormattedRelativeTime value={-value} style="long" unit={unit} numeric="auto">
				{(formattedTime) => (
					<FormattedMessage {...timeUnitAgoMessage} values={{ relativeDate: formattedTime }} />
				)}
			</FormattedRelativeTime>
		</>
	);
};

const getTimeValues = (
	daysSinceUpdate: number,
	leftoverHoursSinceUpdate: number,
	minutesSinceUpdate: number,
): { unit: 'minute' | 'hour' | 'day'; value: number } => {
	if (daysSinceUpdate === 0) {
		if (leftoverHoursSinceUpdate < 1) {
			return { unit: 'minute', value: minutesSinceUpdate };
		}
		return { unit: 'hour', value: leftoverHoursSinceUpdate };
	}
	return { unit: 'day', value: daysSinceUpdate };
};
